import { LitElement, html, css, nothing } from "lit";
import { customElement, property, query, state } from "lit/decorators.js";
import { injectInterFont } from "../utils/fonts";
import type { ChatMessage } from "../widget/state";
import "./typing-indicator";
import "./visitor-form";
import "./scheduling-slots";
import "./action-widgets";

@customElement("cg-message-list")
export class CGMessageList extends LitElement {
  static override styles = css`
    :host {
      display: block;
      height: 100%;
      position: relative; /* anchor fixed button inside shadow */
    }

    .wrap {
      height: 100%;
      overflow-y: auto;
      overscroll-behavior: contain;
      box-sizing: border-box;
      display: flex;
      flex-direction: column;
      align-items: stretch;
      justify-content: flex-start;
      -webkit-overflow-scrolling: touch;

      /* Hide scrollbar for IE, Edge and Firefox */
      -ms-overflow-style: none;
      scrollbar-width: none;
    }

    /* Hide scrollbar for Chrome, Safari and Opera */
    .wrap::-webkit-scrollbar {
      display: none;
    }

    .stack {
      display: flex;
      flex-direction: column;
      gap: 10px;
      padding: 12px;
      min-height: 100%;
    }

    .row {
      display: flex;
    }

    .row.user {
      justify-content: flex-end;
      flex-direction: column;
      align-items: flex-end;
    }
    .row.agent,
    .row.assistant {
      justify-content: flex-start;
      flex-direction: column;
      align-items: flex-start;
    }

    .action-widget-row {
      display: block;
      width: min(100%, 360px);
      max-width: 100%;
      align-self: flex-start;
      flex: 0 0 auto;
      clear: both;
    }

    .action-widget-row cg-scheduling-slots {
      display: block;
      width: 100%;
      max-width: 100%;
      min-width: 0;
    }

    .typing {
      padding: 10px 12px;
      border: 1px solid var(--cg-border);
      border-radius: 14px;
      font-size: 12px;
      color: var(--cg-muted);
    }

    /* ===========================
       Empty State
    ============================ */

    .empty {
      padding: 48px 24px;
      text-align: center;
      color: var(--cg-muted);
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      min-height: 100%;
    }

    .empty-icon {
      font-size: 36px;
      margin-bottom: 12px;
    }

    .empty-title {
      font-size: 18px;
      font-weight: 600;
      color: var(--cg-text);
      margin-bottom: 6px;
    }

    .empty-sub {
      font-size: 14px;
      opacity: 0.7;
      max-width: 260px;
    }

    /* ===========================
       Scroll-To-Bottom Button
    ============================ */

    .jump {
      position: absolute;
      right: 12px;
      bottom: 12px;
      z-index: 20;

      width: 36px;
      height: 36px;
      border-radius: 999px;
      border: none;
      cursor: pointer;

      display: grid;
      place-items: center;

      background: var(--cg-primary);
      color: var(--cg-on-primary);
      box-shadow: 0 6px 18px rgba(0, 0, 0, 0.18);

      transition:
        transform 120ms ease,
        box-shadow 120ms ease,
        opacity 120ms ease;
    }

    .jump:hover {
      transform: translateY(-1px);
      box-shadow: 0 10px 24px rgba(0, 0, 0, 0.22);
    }

    .jump:active {
      transform: scale(0.94);
    }

    .jump sl-icon {
      font-size: 18px;
    }
  `;

  private readonly onSlotSelected = () => {
    requestAnimationFrame(() => this.scrollToBottom("smooth"));
  };

  override connectedCallback(): void {
    super.connectedCallback();
    injectInterFont(this.shadowRoot ?? this);
    this.addEventListener("slot-selected", this.onSlotSelected);
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    this.removeEventListener("slot-selected", this.onSlotSelected);
    this.resizeObserver?.disconnect();
    const el = this.scroller;
    if (el) {
      el.removeEventListener("scroll", this.onScroll);
    }
  }

  @property({ attribute: false }) messages: ChatMessage[] = [];
  @property({ type: Boolean }) roomSelectionDisabled = false;
  @property({ type: Boolean }) streaming = false;
  @property({ type: String }) streamingText = "";
  @property({ type: Array }) suggestedMessages: string[] = [];
  @property({ type: Boolean }) showSuggestions = false;
  @property({ type: Boolean }) escalated = false;

  @query(".wrap") private scroller?: HTMLDivElement;
  @query(".stack") private stack?: HTMLDivElement;

  private resizeObserver?: ResizeObserver;

  /** Show scroll-down button */
  @state() private showJump = false;

  private shouldAutoScroll = false;
  private prevCount = 0;
  private prevStreamingLen = 0;

  /* ============================================================
     Scroll Helpers
  ============================================================ */

  private isNearBottom(): boolean {
    const el = this.scroller;
    if (!el) return true;
    const threshold = 120; // px
    return el.scrollHeight - el.scrollTop - el.clientHeight < threshold;
  }

  private scrollToBottom(mode: "smooth" | "instant" = "instant"): void {
    const el = this.scroller;
    if (!el) return;

    el.scrollTo({
      top: el.scrollHeight,
      behavior: mode === "smooth" ? "smooth" : "auto",
    });
  }

  private jumpToBottom(): void {
    this.scrollToBottom("smooth");
    this.showJump = false;
    this.shouldAutoScroll = true;
  }

  /* ============================================================
     Lifecycle
  ============================================================ */

  override firstUpdated(): void {
    this.bindScroller();

    // Setup ResizeObserver to handle auto-scroll as content grows
    this.resizeObserver = new ResizeObserver(() => {
      if (this.shouldAutoScroll) {
        this.scrollToBottom("smooth");
      }
    });

    if (this.stack) {
      this.resizeObserver.observe(this.stack);
    }
  }

  override async updated(): Promise<void> {
    // Wait until DOM is fully flushed
    await this.updateComplete;

    // Rebind scroller in case DOM switched from empty → messages
    this.bindScroller();

    const count = this.messages.length;
    const streamLen = this.streamingText.length;

    const newMessageArrived = count !== this.prevCount;
    const streamingAdvanced = streamLen !== this.prevStreamingLen;

    /**
     * ✅ Always auto-scroll when a new USER message is added
     * This matches expected chat behavior.
     */
    const isFirstRender = this.prevCount === 0 && count > 0;
    const userJustSentMessage =
      newMessageArrived &&
      count > this.prevCount &&
      this.messages[count - 1]?.role === "USER";

    if (
      !isFirstRender &&
      (userJustSentMessage ||
        ((newMessageArrived || streamingAdvanced) && this.shouldAutoScroll))
    ) {
      // Wait one frame so layout settles before scrolling
      requestAnimationFrame(() => {
        this.scrollToBottom("smooth");
      });
    }

    this.prevCount = count;
    this.prevStreamingLen = streamLen;
  }

  private bindScroller() {
    const el = this.scroller;
    if (!el) return;

    // Prevent double binding
    el.removeEventListener("scroll", this.onScroll);
    el.addEventListener("scroll", this.onScroll);
  }

  private onScroll = () => {
    const nearBottom = this.isNearBottom();
    this.shouldAutoScroll = nearBottom;
    this.showJump = !nearBottom;
  };

  /* ============================================================
     Render
  ============================================================ */

  override render() {
    const hasMessages = this.messages.length > 0 || this.streaming;

    if (!hasMessages) {
      return html`
        <div class="wrap">
          <div class="empty">
            <div class="empty-icon">👋</div>
            <div class="empty-title">Hi! How can I help?</div>
            <div class="empty-sub">Ask a question</div>
          </div>
        </div>
      `;
    }

    const hasUserMessage = this.messages.some(m => m.role === "USER");
    const lastMessage = this.messages[this.messages.length - 1];
    const isLastAgent = lastMessage?.role === "AGENT";
    // const showEscalate = hasUserMessage && isLastAgent && !this.streaming && !this.escalated;
    const showEscalate = false;

    return html`
      <div class="wrap">
        <div class="stack">
          ${this.messages.map(
            (m) => {
              const rowRoleClass = m.role === "USER" ? "user" : "agent";

              return html`
              <div class="row ${rowRoleClass}" style="gap: 4px;">
                <cg-message-bubble
                  .message=${m}
                  .roomSelectionDisabled=${this.streaming || this.escalated || this.roomSelectionDisabled}
                  variant=${m.role}
                  style="display: flex; justify-content: ${m.role === 'USER' ? 'flex-end' : 'flex-start'}; width: 100%;"
                ></cg-message-bubble>
                ${m.role === "AGENT" && m.schedulingSlots?.length
                  ? html`
                      <div class="action-widget-row">
                        <cg-scheduling-slots
                          .slots=${m.schedulingSlots}
                          bookingStatus=${m.bookingStatus ?? "idle"}
                          bookingError=${m.bookingError ?? ""}
                          @confirm-booking=${(e: CustomEvent) => {
                            const event = new CustomEvent("confirm-booking", {
                              detail: { messageId: m.id, ...e.detail },
                              bubbles: true,
                              composed: true,
                            });
                            this.dispatchEvent(event);
                          }}
                        ></cg-scheduling-slots>
                      </div>
                    `
                  : nothing}
              </div>
            `;
            },
          )}
          ${this.streaming
            ? html`
                <div class="row assistant">
                  ${this.streamingText
                    ? html`
                        <cg-message-bubble
                          .message=${{
                            id: "stream",
                            role: "AGENT",
                            content: this.streamingText,
                            createdAt: Date.now(),
                          } as ChatMessage}
                          variant="AGENT"
                        ></cg-message-bubble>
                      `
                    : html`
                        <div class="typing">
                          <cg-typing-indicator></cg-typing-indicator>
                        </div>
                      `}
                </div>
              `
            : nothing}
          ${nothing /* showEscalate
            ? html`
                <div class="row user">
                  <button
                    style="background: var(--cg-bubble-user-bg, var(--cg-chat-bubble)); border: none; border-radius: 20px; padding: 10px 14px; font-size: 14px; color: var(--cg-bubble-user-text, var(--cg-on-primary)); cursor: pointer; opacity: 0.9; transition: opacity 0.2s; max-width: 82%; font-family: inherit; line-height: 1.45;"
                    onmouseover="this.style.opacity='1'"
                    onmouseout="this.style.opacity='0.9'"
                    @click=${(e: Event) => {
                      const btn = e.currentTarget as HTMLButtonElement;
                      btn.textContent = "Escalating...";
                      btn.disabled = true;
                      btn.style.opacity = "0.7";
                      const event = new CustomEvent("escalate", { bubbles: true, composed: true });
                      this.dispatchEvent(event);
                      setTimeout(() => { btn.textContent = "Escalated to human"; }, 800);
                    }}
                  >
                    Escalate to a human
                  </button>
                </div>
              `
            : nothing */}
          ${this.showSuggestions &&
          !this.streaming &&
          this.suggestedMessages.length > 0
            ? html`
                <div
                  class="row assistant"
                  style="margin-top: auto; justify-content: flex-start; padding: 32px 12px 24px;"
                >
                  <cg-suggestions
                    .items=${this.suggestedMessages}
                    .onSelect=${(v: string) => {
                      const event = new CustomEvent("suggestion-select", {
                        detail: { value: v },
                        bubbles: true,
                        composed: true,
                      });
                      this.dispatchEvent(event);
                    }}
                  ></cg-suggestions>
                </div>
              `
            : nothing}
        </div>
      </div>

      ${this.showJump
        ? html`
            <button
              class="jump"
              title="Scroll to latest"
              aria-label="Scroll to latest"
              @click=${() => this.jumpToBottom()}
            >
              <sl-icon name="arrow-down-short"></sl-icon>
            </button>
          `
        : nothing}
    `;
  }
}
